import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { PLANS } from '../lib/config.js';
import { tokens as tokensApi, admin } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import { purchasesAvailable, purchase, currentStore, STORE_LABEL } from '../lib/purchases.js';
import { isNative } from '../lib/supabase.js';
import { Glass, Button, Tag, Empty, Notice } from '../components/ui.jsx';

const money = (c) => `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`;

const FEATURES = {
  basic:   ['One device held', '100 tokens each month', 'One transfer monthly'],
  premium: ['Two devices held', '200 tokens each month', 'Two transfers monthly', 'Priority on the line'],
  crystal: ['Three devices held', '500 tokens each month', 'Three transfers monthly', 'Full reserve'],
};

const TABS = [['plans', 'Covenants'], ['tokens', 'Tokens']];

export default function SubscriptionPage() {
  const { user, subscription, hasActiveSubscription, tokens, refresh, isAdmin } = useSession();
  const [tab, setTab] = useState(() =>
    new URLSearchParams(location.search).get('tab') === 'tokens' ? 'tokens' : 'plans');
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { tokensApi.packages().then(setPackages).catch(console.error); }, []);

  // Keepers (admins) take plans and tokens without a store in between: the
  // same admin RPCs the Profile page uses, written to the ledger as grants.
  // The server re-checks the role on every call, so this is a shortcut, not a
  // permission — a non-admin forcing this path gets 42501 back.
  const keeper = isAdmin;
  const buy = async (sku) => {
    setBusy(sku); setError(null);
    try {
      if (keeper) {
        const tier = sku.match(/\.sub\.(\w+)$/)?.[1];
        if (tier) await admin.grantSubscription(user.id, tier, 30, `keeper purchase ${sku}`);
        else {
          const pack = packages.find((p) => p.sku === sku);
          if (!pack) throw new Error('Unknown offering.');
          await admin.grantTokens(user.id, pack.tokens, `keeper purchase ${sku}`);
        }
        await refresh();
      } else {
        await purchase(sku);
        setTimeout(refresh, 1500);
      }
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const held = hasActiveSubscription && subscription ? PLANS[subscription.tier]?.name : null;
  // One customer, three stores. A plan bought elsewhere is changed there, not
  // here — otherwise a second subscription could be opened in another store.
  const heldStore = hasActiveSubscription ? subscription?.rc_store : null;
  const lockedElsewhere = Boolean(heldStore) && heldStore !== 'promotional' && heldStore !== currentStore();
  const canBuy = purchasesAvailable() || keeper;
  const canBuyPlans = canBuy && !lockedElsewhere;

  return (
    <div>
      <header className="rise rise-1 pt-2 pb-6 md:pb-8">
        <p className="eyebrow">Your covenant</p>
        <h1 className="display text-[2.6rem] md:text-[3.4rem] mt-2">
          {held ? <>Holding <span className="display-i lit">{held}</span>.</> : <>Currently <span className="display-i lit">unbound</span>.</>}
        </h1>
        <p className="text-sm mt-4" style={{ color: 'var(--text-2)' }}>
          {hasActiveSubscription
            ? `Renews ${new Date(subscription.current_period_end).toLocaleDateString(undefined,
                { day: 'numeric', month: 'short', year: 'numeric' })}`
            : 'No active covenant'}
          <span style={{ color: 'var(--text-3)' }}> · </span>
          <span className="font-semibold nums" style={{ color: 'var(--gold)' }}>{tokens} tokens</span> held
        </p>
      </header>

      <div className="rise rise-2 mb-6 flex items-center gap-1 p-1 rounded-full w-fit" style={{ background: 'var(--glass)' }}>
        {TABS.map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)}
                  className="relative px-4 py-2 text-sm font-semibold rounded-full transition-colors"
                  style={{ color: tab === k ? 'var(--text)' : 'var(--text-3)' }}>
            {tab === k && (
              <motion.span layoutId="sub-tab-pill" className="absolute inset-0 rounded-full"
                style={{ background: 'var(--glass-3)', boxShadow: 'inset 0 1px 0 var(--edge-lit)' }}
                transition={{ type: 'spring', stiffness: 400, damping: 34 }} />
            )}
            <span className="relative">{lbl}</span>
          </button>
        ))}
      </div>

      {error && (
        <p className="rise rise-3 text-sm mb-4 px-3.5 py-2.5 rounded-xl"
           style={{ color: 'var(--rust)', background: 'color-mix(in oklab, var(--rust) 12%, transparent)' }}>
          {error}
        </p>
      )}
      {keeper ? (
        <div className="rise rise-3 mb-6">
          <Notice tone="building">
            Keeper account: anything you take here is granted by the server at no charge and written to the ledger as a grant.
          </Notice>
        </div>
      ) : !purchasesAvailable() && (
        <div className="rise rise-3 mb-6">
          <Notice tone="violet">
            {isNative() ? 'Purchases are not available in this build.'
                        : 'Web checkout is opening soon. Plans and tokens are available now in the iOS and Android apps.'}
          </Notice>
        </div>
      )}
      {lockedElsewhere && tab === 'plans' && (
        <div className="rise rise-3 mb-6">
          <Notice tone="violet">
            Your plan is held through {STORE_LABEL[heldStore] ?? heldStore}. Change or cancel it there — it applies here the moment it does.
          </Notice>
        </div>
      )}

      {tab === 'plans' ? (
        <div className="rise rise-4 grid md:grid-cols-3 gap-4 md:items-start">
          {Object.values(PLANS).map((plan, i) => {
            const current = subscription?.tier === plan.tier && hasActiveSubscription;
            const lit = i === 1;
            return (
              <Glass key={plan.tier} className={`p-7 flex flex-col ${lit ? 'md:-translate-y-3' : ''}`}
                     style={lit ? { borderColor: 'color-mix(in oklab, var(--clay) 55%, transparent)',
                                    boxShadow: 'var(--glow-clay), var(--shadow)' } : undefined}>
                <div className="flex items-center justify-between">
                  <p className="eyebrow">{['I', 'II', 'III'][i]}</p>
                  {current
                    ? <Tag tone="active" dot>Held</Tag>
                    : lit && (
                      <span className="text-[0.65rem] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full"
                            style={{ background: 'var(--clay)', color: '#fff' }}>Most held</span>
                    )}
                </div>
                <h3 className="display text-3xl mt-3">{plan.name}</h3>
                <p className="display text-5xl mt-5">
                  {money(plan.priceCents)}
                  <span className="text-sm ml-1.5 font-sans" style={{ color: 'var(--text-3)' }}>/ month</span>
                </p>
                <ul className="mt-7 space-y-2.5 text-sm flex-1" style={{ color: 'var(--text-2)' }}>
                  {FEATURES[plan.tier].map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check size={14} className="mt-0.5 shrink-0" style={{ color: 'var(--sage)' }} />{f}
                    </li>
                  ))}
                </ul>
                <Button variant={current ? 'quiet' : lit ? 'primary' : 'glass'} className="mt-8"
                        disabled={current || busy === plan.tier || !canBuyPlans}
                        onClick={() => buy(`com.waserd.field.sub.${plan.tier}`)}>
                  {busy === plan.tier ? <Loader2 size={15} className="animate-spin" />
                    : current ? 'Current covenant' : 'Take this'}
                </Button>
              </Glass>
            );
          })}
        </div>
      ) : (
        <div className="rise rise-4 grid grid-cols-2 md:grid-cols-1 gap-3">
          {packages.length === 0 && <Empty className="col-span-full">No offerings available right now.</Empty>}
          {/* Phone: a 2-up grid of compact cards. Desktop: one row per pack. */}
          {packages.map((p) => (
            <Glass key={p.id} hover className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5 p-4 md:p-6">
              <div className="md:shrink-0 md:text-center md:min-w-[4.5rem]">
                <p className="display text-4xl nums leading-none" style={{ color: 'var(--gold)' }}>{p.tokens}</p>
                <p className="eyebrow mt-1.5" style={{ letterSpacing: '0.1em' }}>tokens</p>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="display text-xl md:text-2xl">{p.name}</h3>
                  {p.is_popular && <Tag tone="warn" dot>Most taken</Tag>}
                </div>
                <p className="hidden md:block text-sm mt-1.5 leading-relaxed" style={{ color: 'var(--text-2)' }}>{p.description}</p>
              </div>
              <Button variant={p.is_popular ? 'gold' : 'glass'} className="w-full md:w-auto md:shrink-0 !px-4"
                      disabled={busy === p.sku || !canBuy} onClick={() => buy(p.sku)}>
                {busy === p.sku ? <Loader2 size={15} className="animate-spin" /> : keeper ? 'Take · free' : money(p.price_cents)}
              </Button>
            </Glass>
          ))}
        </div>
      )}

      <p className="rise rise-5 text-xs leading-relaxed mt-8 max-w-lg" style={{ color: 'var(--text-3)' }}>
        {isNative()
          ? 'Offerings are verified with Apple or Google before your balance moves.'
          : 'Payments are processed securely by Stripe and verified before your balance moves.'}
        {' '}Tokens are granted by the server, never by this device. The same plan works in every app you sign into.
      </p>
    </div>
  );
}
